/**
 * DefenderFormationBot - Defender for IMPOSSIBLE mode.
 * Extends FormationBot with marking, interception, and clearing behaviors.
 */

import * as BABYLON from "babylonjs";
import { Ball } from "./Ball";
import { Player } from "./Player";
import { FormationBot } from "./FormationBot";
import { BotBase } from "./BotBase";
import { AIRole, AIState, AllowedZone, SeparationConfig } from "../systems/DifficultyManager";
import { KickType } from "./HumanoidMesh";

// Defender-specific constants
const CLEAR_POWER = 12;
const PASS_POWER = 10;
const PASS_COOLDOWN = 1.2;
const MARKING_RANGE = 8;
const INTERCEPT_RANGE = 10;

/**
 * Formation defender bot for IMPOSSIBLE mode
 * - Zone: defensive third
 * - States: MARK, INTERCEPT, CLEAR, PASS, FORMATION_RETURN
 * - Behavior: Mark attackers, intercept passes, clear danger
 */
export class DefenderFormationBot extends FormationBot {
  private _passTimer = 0;

  constructor(
    scene: BABYLON.Scene,
    ball: Ball,
    role: AIRole,
    homePosition: BABYLON.Vector3,
    allowedZone: AllowedZone,
    separationConfig: SeparationConfig,
    shadowGenerator?: BABYLON.ShadowGenerator,
    player?: Player
  ) {
    super(
      scene,
      ball,
      role,
      homePosition,
      allowedZone,
      separationConfig,
      shadowGenerator,
      player,
      `defenderFormation_${role}`
    );
    
    // Set stretch for defenders
    this.setStretch(8, 10);
  }

  /**
   * Purple jersey color for formation defenders
   */
  protected _getJerseyColor(): BABYLON.Color3 {
    return new BABYLON.Color3(0.5, 0.2, 0.7); // Purple jersey
  }

  /**
   * Update with pass timer
   */
  update(dt: number, teammates: BotBase[]): void {
    if (this._passTimer > 0) this._passTimer -= dt;
    super.update(dt, teammates);
  }

  /**
   * Defender-specific decision making
   */
  protected _makeDecision(
    distToBall: number,
    distToGoal: number,
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3
  ): void {
    // Check if ball is in danger zone (near our goal)
    const ballInDanger = ballPos.z > 15 && Math.abs(ballPos.x) < 15;

    // If we have possession, look to clear or pass
    if (this.hasBallPossession && this._passTimer <= 0) {
      if (ballInDanger) {
        this._state = AIState.CLEAR;
      } else {
        this._state = AIState.PASS;
      }
      return;
    }

    // If we have possession but can't act yet, dribble
    if (this.hasBallPossession) {
      this._state = AIState.DRIBBLE;
      return;
    }

    // Tackle if player has ball and we're close
    if (this._player && this._player.hasBallPossession && distToBall < 2.5 && this._tackleCooldown <= 0) {
      this._state = AIState.TACKLE;
      return;
    }

    // Check for interception opportunity
    if (this._canIntercept(ballPos, myPos)) {
      this._state = AIState.INTERCEPT;
      return;
    }

    // If ball is in our defensive zone, chase it
    if (ballPos.z > this._allowedZone.minZ) {
      this._state = AIState.CHASE_BALL;
      return;
    }

    // If player is in our zone, mark them
    if (this._player && this._player.mesh.position.z > 10) {
      this._state = AIState.MARK;
      return;
    }

    // Check if should return to formation
    if (this._shouldReturnToFormation(ballPos, myPos)) {
      this._state = AIState.FORMATION_RETURN;
      return;
    }

    // Default: hold position
    this._state = AIState.HOLD_POSITION;
  }

  /**
   * Handle state behavior with defender-specific states
   */
  protected _handleState(
    ballPos: BABYLON.Vector3,
    myPos: BABYLON.Vector3,
    distToBall: number,
    distToGoal: number,
    dt: number
  ): void {
    switch (this._state) {
      case AIState.MARK:
        this._handleMark(ballPos, myPos, dt);
        break;
      case AIState.INTERCEPT:
        this._handleIntercept(ballPos, myPos, dt);
        break;
      case AIState.CLEAR:
        this._handleClear(ballPos, myPos);
        break;
      case AIState.PASS:
        this._handlePass(ballPos, myPos);
        break;
      default:
        super._handleState(ballPos, myPos, distToBall, distToGoal, dt);
    }
  }

  /**
   * Mark the player - position between player and goal
   */
  private _handleMark(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    if (!this._player) {
      this._state = AIState.HOLD_POSITION;
      return;
    }

    const playerPos = this._player.mesh.position;
    const goalPos = new BABYLON.Vector3(0, 0, 30);
    
    // Position between player and goal
    const toGoal = goalPos.subtract(playerPos).normalize();
    const targetPos = playerPos.add(toGoal.scale(3));
    
    // Clamp to our zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
    
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = 5.5 * 0.8;
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    // Face the player
    const toPlayer = playerPos.subtract(myPos);
    const angle = Math.atan2(toPlayer.x, toPlayer.z);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.1);
  }

  /**
   * Check if we can intercept the ball
   */
  private _canIntercept(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): boolean {
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    if (!ballVel || ballVel.length() < 3) return false;
    
    // Check if ball is moving toward our goal
    if (ballVel.z < 2) return false;
    
    // Check if we're in a position to intercept
    const distToBall = BABYLON.Vector3.Distance(myPos, ballPos);
    return distToBall < INTERCEPT_RANGE;
  }

  /**
   * Intercept the ball
   */
  private _handleIntercept(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3, dt: number): void {
    const ballVel = this._ball.mesh.physicsImpostor?.getLinearVelocity();
    
    let targetPos: BABYLON.Vector3;
    if (ballVel && ballVel.length() > 2) {
      // Predict ball position
      const ballDir = new BABYLON.Vector3(ballVel.x, 0, ballVel.z).normalize();
      const timeToIntercept = BABYLON.Vector3.Distance(myPos, ballPos) / Math.max(ballVel.length(), 1);
      targetPos = ballPos.add(ballDir.scale(timeToIntercept * 3));
    } else {
      targetPos = ballPos.clone();
    }
    
    // Clamp to zone
    targetPos.x = BABYLON.Scalar.Clamp(targetPos.x, this._allowedZone.minX, this._allowedZone.maxX);
    targetPos.z = BABYLON.Scalar.Clamp(targetPos.z, this._allowedZone.minZ, this._allowedZone.maxZ);
    
    const dx = targetPos.x - myPos.x;
    const dz = targetPos.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.5) {
      const speed = 7.5; // Sprint to intercept
      myPos.x += (dx / dist) * speed * dt;
      myPos.z += (dz / dist) * speed * dt;
      this._currentSpeed = speed;
    } else {
      this._currentSpeed = 0;
    }
    
    const angle = Math.atan2(dx, dz);
    this.mesh.rotation.y = BABYLON.Scalar.Lerp(this.mesh.rotation.y, angle, 0.15);
    
    // If close to ball, gain possession
    const distToBall = BABYLON.Vector3.Distance(myPos, ballPos);
    if (distToBall < 2.0) {
      this.hasBallPossession = true;
      this._state = AIState.CLEAR;
    }
  }

  /**
   * Clear the ball from danger
   */
  private _handleClear(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;
    
    // Clear to the sides (away from goal)
    const clearX = myPos.x > 0 ? 15 : -15;
    const clearDir = new BABYLON.Vector3(clearX - myPos.x, 0.2, -20 - myPos.z).normalize();
    
    this._ball.kick(clearDir, CLEAR_POWER);
    
    this._kickTimer = PASS_COOLDOWN;
    this._passTimer = PASS_COOLDOWN;
    this.animator.triggerKick(KickType.Power);
    this.hasBallPossession = false;
    this._state = AIState.HOLD_POSITION;
  }

  /**
   * Pass to midfielder
   */
  private _handlePass(ballPos: BABYLON.Vector3, myPos: BABYLON.Vector3): void {
    if (this._kickTimer > 0) return;
    
    // Pass forward to midfielder position
    const passTarget = new BABYLON.Vector3(
      myPos.x * 0.5,
      0,
      5 // Midfield area
    );
    
    const dx = passTarget.x - myPos.x;
    const dz = passTarget.z - myPos.z;
    const dist = Math.sqrt(dx * dx + dz * dz);
    
    if (dist > 0.1) {
      const passDir = new BABYLON.Vector3(dx / dist, 0.1, dz / dist).normalize();
      this._ball.kick(passDir, PASS_POWER);
      
      this._kickTimer = PASS_COOLDOWN;
      this._passTimer = PASS_COOLDOWN;
      this.animator.triggerKick(KickType.Normal);
      this.hasBallPossession = false;
      this._state = AIState.HOLD_POSITION;
    }
  }

  /**
   * Reset pass timer
   */
  resetPosition(): void {
    super.resetPosition();
    this._passTimer = 0;
  }
}